import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, ShoppingBag, Heart, Share2, Truck, ShieldCheck, RotateCcw, Minus, Plus } from "lucide-react";
import { getProduct, getCollection, money, api } from "../lib/api";
import { useCjCart } from "../context/CjCartContext";
import { useAuth } from "../context/AuthContext";
import { ProductCard } from "../components/ProductCard";
import { Reviews } from "../components/Reviews";
import { ConfidenceScore } from "../components/ConfidenceScore";
import { toast } from "sonner";

const RECENT_KEY = "taven_recent";

export const ProductDetail = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { add } = useCjCart();
  const { user, authApi } = useAuth();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    let active = true;
    setProduct(null); setNotFound(false); setActiveImg(0); setQty(1);
    getProduct(handle)
      .then((p) => {
        if (!active) return;
        setProduct(p);
        // recently viewed
        try {
          const stored = JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
          const others = stored.filter((x) => x.handle !== p.handle);
          setRecent(others.slice(0, 6));
          localStorage.setItem(RECENT_KEY, JSON.stringify([{ handle: p.handle, title: p.title, image: p.image, price: p.price, currency: p.currency, defaultVariantId: p.defaultVariantId, onSale: p.onSale, compareAtPrice: p.compareAtPrice }, ...others].slice(0, 8)));
        } catch (err) { console.error("Failed to update recently viewed:", err); }
      })
      .catch(() => { if (active) setNotFound(true); });
    getCollection("best-sellers", 8).then((d) => { if (active) setRelated((d.products || []).slice(0, 4)); }).catch(() => {});
    return () => { active = false; };
  }, [handle]);

  if (notFound) return <div className="max-w-7xl mx-auto py-24 text-center" data-testid="product-not-found"><h1 className="text-2xl font-display font-black">Product not found</h1><Link to="/shop" className="text-taven-gold mt-3 inline-block">Back to Shop</Link></div>;
  if (!product) return <div className="max-w-7xl mx-auto py-16 px-4"><div className="grid lg:grid-cols-2 gap-10"><div className="aspect-square skeleton" /><div className="space-y-4"><div className="h-8 w-3/4 skeleton" /><div className="h-6 w-1/3 skeleton" /><div className="h-24 skeleton" /></div></div></div>;

  const images = product.images?.length ? product.images : (product.image ? [{ url: product.image }] : []);

  const doAdd = () => { add(product, qty, "shopify"); toast.success("Added to cart", { description: product.title }); };
  const doBuyNow = () => {
    add(product, qty, "shopify");
    navigate("/market/cart");
  };
  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied to clipboard"); };
  const saveWishlist = async () => {
    if (!user) { toast.error("Please sign in to save to your wishlist"); navigate("/login", { state: { from: `/product/${handle}` } }); return; }
    try {
      await authApi.post("/customer/wishlist", { handle: product.handle, title: product.title, image: product.image, price: product.price });
      toast.success("Saved to your wishlist");
    } catch { toast.error("Could not save to wishlist"); }
  };

  return (
    <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-7xl py-8" data-testid="product-detail-page">
      <nav className="text-xs text-muted-foreground mb-6"><Link to="/" className="hover:text-black">Home</Link> / <Link to="/shop" className="hover:text-black">Shop</Link> / <span className="text-black">{product.title}</span></nav>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        <div>
          <div className="aspect-square bg-taven-gray overflow-hidden">
            {images[activeImg] && <img src={images[activeImg].url} alt={product.title} className="w-full h-full object-cover" />}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {images.map((im, i) => (
                <button key={im.url || i} onClick={() => setActiveImg(i)} className={`w-20 h-20 shrink-0 bg-taven-gray overflow-hidden border-2 ${activeImg === i ? "border-taven-gold" : "border-transparent"}`}><img src={im.url} alt={`${product.title} view ${i + 1}`} className="w-full h-full object-cover" /></button>
              ))}
            </div>
          )}
        </div>

        <div>
          {product.onSale && <span className="inline-block bg-taven-red text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 mb-3">Sale</span>}
          <h1 className="text-2xl md:text-3xl font-black font-display tracking-tight">{product.title}</h1>
          <a href="#reviews" className="flex items-center gap-1 mt-2 hover:opacity-80" data-testid="product-rating-link">{[0,1,2,3,4].map((i) => <Star key={i} className="w-4 h-4 fill-taven-gold text-taven-gold" />)}<span className="text-xs text-muted-foreground ml-1">Read customer reviews</span></a>
          <div className="flex items-baseline gap-3 mt-4">
            <span className="text-3xl font-black">{money(product.price, product.currency)}</span>
            {product.compareAtPrice && <span className="text-lg text-gray-400 line-through">{money(product.compareAtPrice, product.currency)}</span>}
          </div>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2"><Truck className="w-4 h-4 text-taven-gold" /> Ships worldwide · Estimated 7–15 business days</p>

          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center border border-black/20">
              <button onClick={() => setQty(Math.max(1, qty-1))} className="p-3 hover:bg-taven-gray" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
              <span className="px-5 font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty+1)} className="p-3 hover:bg-taven-gray" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={share} data-testid="share-button" className="p-3 border border-black/20 hover:bg-black hover:text-white transition-colors"><Share2 className="w-4 h-4" /></button>
            <button onClick={saveWishlist} data-testid="wishlist-button" className="p-3 border border-black/20 hover:bg-black hover:text-white transition-colors"><Heart className="w-4 h-4" /></button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            {product.soldOut ? (
              <BackInStock handle={product.handle} title={product.title} defaultEmail={user?.email} />
            ) : (
              <>
                <button onClick={doAdd} data-testid="detail-add-to-cart" className="flex-1 bg-taven-black text-white font-bold uppercase tracking-wider py-4 hover:bg-taven-orange transition-colors flex items-center justify-center gap-2"><ShoppingBag className="w-5 h-5" /> Add to Cart</button>
                <button onClick={doBuyNow} data-testid="detail-buy-now" className="flex-1 bg-taven-orange text-white font-bold uppercase tracking-wider py-4 hover:bg-[#d9561a] transition-colors">Buy Now</button>
              </>
            )}
          </div>

          <ConfidenceScore handle={product.handle} />

          <div className="mt-6 border border-black/10 divide-y divide-black/5" data-testid="trust-layer">
            {[
              { i: Truck, t: "Estimated delivery", d: "7–15 business days · Tracked via TAVEN Tracking Center" },
              { i: ShieldCheck, t: "Secure checkout", d: "Encrypted payment · Buyer protection on eligible orders" },
              { i: RotateCcw, t: "Easy returns", d: "Approved returns within policy windows" },
              { i: Star, t: "Verified reviews", d: "Reviews from confirmed TAVEN purchasers" },
            ].map((r) => (
              <div key={r.t} className="flex items-start gap-3 px-4 py-3">
                <r.i className="w-4 h-4 text-taven-gold shrink-0 mt-0.5" />
                <div><p className="text-sm font-bold">{r.t}</p><p className="text-xs text-muted-foreground">{r.d}</p></div>
              </div>
            ))}
            <div className="px-4 py-3 flex items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 bg-taven-gold/15 text-taven-gold font-bold uppercase tracking-wider px-2 py-1"><ShieldCheck className="w-3.5 h-3.5" /> TAVEN Quality Checked</span>
              <Link to="/contact" className="text-muted-foreground hover:text-black underline">Questions? Contact TAVEN support</Link>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-6">
            {[{i:ShieldCheck,t:"Secure Checkout"},{i:Truck,t:"Global Shipping"},{i:RotateCcw,t:"Easy Returns"}].map((b) => (
              <div key={b.t} className="flex flex-col items-center gap-1 p-3 bg-taven-gray/60 text-center"><b.i className="w-5 h-5 text-taven-gold" /><span className="text-[10px] font-bold uppercase tracking-wider">{b.t}</span></div>
            ))}
          </div>

          {product.description && (
            <div className="mt-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] mb-2">Description</h3>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line line-clamp-[12]">{product.description}</p>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-6 border-t border-black/10 pt-4"><Link to="/policy/return" className="underline">Return &amp; Shipping Policy</Link> — Approved returns accepted within policy windows. See full policy for details.</p>
        </div>
      </div>

      <div id="reviews"><Reviews kind="product" target={product.handle} /></div>

      {related.length > 0 && (
        <section className="mt-16"><h2 className="text-2xl font-black font-display mb-6">Related Products</h2><div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">{related.map((p) => <ProductCard key={p.id} product={p} />)}</div></section>
      )}
      {recent.length > 0 && (
        <section className="mt-16"><h2 className="text-2xl font-black font-display mb-6">Recently Viewed</h2><div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">{recent.map((p) => <ProductCard key={p.handle} product={p} />)}</div></section>
      )}
    </div>
  );
};

const BackInStock = ({ handle, title, defaultEmail }) => {
  const [email, setEmail] = useState(defaultEmail || "");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notify/back-in-stock", { email, product_handle: handle, title });
      setDone(true);
      toast.success("We'll email you when it's back in stock");
    } catch { toast.error("Could not subscribe. Please try again."); }
  };
  return (
    <div className="flex-1 border border-black/15 p-4" data-testid="back-in-stock">
      <p className="text-sm font-bold flex items-center gap-2"><span className="inline-block bg-taven-gray text-black text-[10px] font-bold uppercase tracking-wider px-2 py-1">Sold Out</span> Get notified when available</p>
      {done ? (
        <p className="text-sm text-muted-foreground mt-3" data-testid="back-in-stock-done">You're on the list. We'll email you the moment it returns.</p>
      ) : (
        <form onSubmit={submit} className="flex gap-2 mt-3">
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" data-testid="back-in-stock-email" className="flex-1 border border-black/15 focus:border-taven-gold px-3 py-2.5 text-sm outline-none" />
          <button data-testid="back-in-stock-submit" className="bg-taven-black text-white font-bold uppercase tracking-wider text-xs px-4 hover:bg-taven-orange transition-colors">Notify Me</button>
        </form>
      )}
    </div>
  );
};

export default ProductDetail;
